/*
  assets/b-filters.js  (#443)  Custom element <b-filters> for blocks/b-filters.liquid.

  A change to a checkbox, the sort menu or a price box builds the new filter URL (empty price
  boxes and the default sort left out) and emits a cancelable `b-filters:change` (bubbles,
  detail.params). sections/b-collection's element cancels it and updates the page in place;
  if nothing cancels it, the page reloads at the new URL. Without this script the form still
  works through its Apply button.

  Desktop: the panel stays open. Phone: the panel opens as a full-screen drawer. The same
  "Filters (N)" button (the <summary>, its arrow down to open and up to close) stays at the top
  with a Clear all link under it; that button, Escape and the backdrop close it. Focus stays on the
  button, Tab stays inside the drawer, and the page behind does not scroll.
*/
if (!customElements.get('b-filters')) {
  class BFilters extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.panel = this.querySelector('details');
      this.summary = this.panel ? this.panel.querySelector(':scope > summary') : null;
      if (!this.form || !this.panel) return;

      const apply = this.form.querySelector('[data-apply]');
      if (apply) apply.hidden = true;
      this.setAttribute('data-enhanced', '');

      this.desktop = window.matchMedia('(min-width: 990px)');
      this.panel.open = this.desktop.matches;

      this.onMedia = () => {
        if (this.desktop.matches) {
          this.panel.open = true;
          this.setDrawer(false);
        } else {
          this.panel.open = false;
        }
      };
      this.onToggle = () => {
        if (this.desktop.matches) {
          if (!this.panel.open) this.panel.open = true;
          return;
        }
        this.setDrawer(this.panel.open);
      };
      this.onChange = (event) => {
        // A Metafield filters panel (#541) handles its own changes in assets/b-product-filters.js.
        // Checked on the panel, not the box: that panel redraws its options before this runs, so
        // the ticked box is no longer in the page and a full reload would follow (Lee, 2026-10-15).
        if (this.querySelector('b-product-filters')) return;
        if (event.target.matches('input, select')) this.submit();
      };
      this.onClick = (event) => {
        if (event.target.closest('[data-close]')) {
          event.preventDefault();
          this.panel.open = false;
        }
      };
      // While the drawer is open: Escape closes it from anywhere, and Tab stays inside it.
      this.onKeydown = (event) => {
        if (!this.hasAttribute('data-drawer-open')) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.panel.open = false;
        } else if (event.key === 'Tab') {
          // The toggle is position: fixed, so visibility is read from its boxes, not offsetParent.
          const items = [...this.querySelectorAll('summary, a[href], button:not([disabled]), input:not([disabled]), select:not([disabled])')]
            .filter((el) => el.getClientRects().length > 0);
          if (!items.length) return;
          const first = items[0];
          const last = items[items.length - 1];
          if (!items.includes(document.activeElement)) {
            event.preventDefault();
            first.focus();
          } else if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
      };

      this.desktop.addEventListener('change', this.onMedia);
      this.panel.addEventListener('toggle', this.onToggle);
      this.form.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      document.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      if (!this.form) return;
      this.setDrawer(false);
      this.desktop.removeEventListener('change', this.onMedia);
      this.panel.removeEventListener('toggle', this.onToggle);
      this.form.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('keydown', this.onKeydown);
    }

    setDrawer(open) {
      const wasOpen = this.hasAttribute('data-drawer-open');
      if (open === wasOpen) return;
      this.toggleAttribute('data-drawer-open', open);
      document.documentElement.classList.toggle('b-filters-drawer-open', open);
      // The open drawer is a modal dialog named by its heading; Tab stays inside it (onKeydown)
      // and everything outside it is inert until it closes.
      this.labelDialog();
      this.isolate(open);
      // Focus stays on (or returns to) the Filters toggle.
      if (this.summary && (open || this.contains(document.activeElement))) this.summary.focus();
    }

    // Names the open drawer as a dialog: this element, so the toggle at the top is inside it.
    // b-collection calls it again after a swap.
    labelDialog() {
      const open = this.hasAttribute('data-drawer-open');
      const heading = this.form.querySelector('.b-filters__heading');
      if (open) {
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        if (heading) {
          heading.id = `${this.form.id}-title`;
          this.setAttribute('aria-labelledby', heading.id);
        }
      } else {
        this.removeAttribute('role');
        this.removeAttribute('aria-modal');
        this.removeAttribute('aria-labelledby');
      }
    }

    // Marks every element outside the drawer inert (siblings of each ancestor up to <body>).
    isolate(on) {
      if (on) {
        this.inerted = [];
        let node = this;
        while (node && node !== document.body) {
          const parent = node.parentElement;
          if (!parent) break;
          for (const sibling of parent.children) {
            // The backdrop stays live so a tap on it closes the drawer.
            if (sibling !== node && !sibling.inert && sibling.tagName !== 'SCRIPT' && !sibling.matches('.b-filters__backdrop')) {
              sibling.inert = true;
              this.inerted.push(sibling);
            }
          }
          node = parent;
        }
      } else if (this.inerted) {
        this.inerted.forEach((el) => { el.inert = false; });
        this.inerted = null;
      }
    }

    // Native constraints (min, max, step) plus minimum <= maximum; an empty bound is allowed.
    valid() {
      const min = this.form.querySelector('[data-price="min"]');
      const max = this.form.querySelector('[data-price="max"]');
      if (max) {
        const inverted = min && min.value !== '' && max.value !== '' && Number(min.value) > Number(max.value);
        max.setCustomValidity(inverted ? 'The maximum must be at least the minimum.' : '');
      }
      // A closed accordion hides its inputs, and the browser can't point at a hidden invalid one.
      this.form.querySelectorAll('details:not([open])').forEach((d) => {
        if (d.querySelector(':invalid')) d.open = true;
      });
      return this.form.reportValidity();
    }

    submit() {
      if (!this.valid()) return;
      const params = new URLSearchParams();
      const sort = this.form.querySelector('select[name="sort_by"]');
      for (const [key, value] of new FormData(this.form)) {
        if (value === '') continue;
        // The collection's own default order needs no parameter.
        if (key === 'sort_by' && sort && value === sort.dataset.default) continue;
        params.append(key, value);
      }
      const query = params.toString();
      const event = new CustomEvent('b-filters:change', { bubbles: true, cancelable: true, detail: { params: query } });
      if (!this.dispatchEvent(event)) return;
      const url = new URL(this.form.action, window.location.origin);
      url.search = query;
      window.location.assign(url.toString());
    }
  }

  customElements.define('b-filters', BFilters);
}
